// Kalite: şikayet kanbanı ve detayı, puanlar, kalite paneli (C49–C52, B17–B18): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Yolcu tarafı şikayet girdisi/yanıtı (`ComplaintInput`, `ComplaintItem`) ve puan etiketleri `api/passenger.ts`'tedir.
import { z } from 'zod';
import {
  COMPLAINT_CATEGORIES,
  COMPLAINT_STATUSES,
  type ComplaintCategory,
  type ComplaintStatus,
  type Direction,
  type TripStatus,
} from '../enums';
import { canTransitionComplaint } from '../state';
import { Id, type Paged } from './common';
import { RATING_TAGS, type RatingTag } from './passenger';

// ───────── durumlar, olaylar ─────────

/** Kanban sütun başlıkları (Control). Yolcu uygulaması `COMPLAINT_STATUS_LABELS` kullanır. */
export const COMPLAINT_BOARD_LABELS: Record<ComplaintStatus, string> = {
  NEW: 'Yeni',
  REVIEWING: 'İnceleniyor',
  WAITING_DRIVER: 'Şoför yanıtı bekleniyor',
  RESOLVED: 'Çözüldü',
  REJECTED: 'Reddedildi',
};

export const OPEN_COMPLAINT_STATUSES: ComplaintStatus[] = ['NEW', 'REVIEWING', 'WAITING_DRIVER'];
export const CLOSED_COMPLAINT_STATUSES: ComplaintStatus[] = ['RESOLVED', 'REJECTED'];

/** Güvenlik kategorileri: operasyona uyarı düşer, ilk yanıt süresi kısadır, detayda kritik bant gösterilir. */
export const SAFETY_COMPLAINT_CATEGORIES: ComplaintCategory[] = ['DANGEROUS_DRIVING', 'DRIVER_BEHAVIOR'];

/** Şikayetin izin verilen sonraki durumları (aynı durum hariç). */
export function nextComplaintStatuses(from: ComplaintStatus): ComplaintStatus[] {
  return COMPLAINT_STATUSES.filter((s) => s !== from && canTransitionComplaint(from, s));
}

/**
 * Olay türleri (ComplaintEvent.type). Geçmiş değiştirilemez; düzeltme yeni olayla yapılır.
 * Görünürlük: yolcu `PASSENGER_COMPLAINT_EVENTS`, kurum `INSTITUTION_COMPLAINT_EVENTS`, firma hepsini görür.
 */
export const COMPLAINT_EVENT_TYPES = [
  'CREATED',
  'STATUS',
  'REPLY',
  'NOTE',
  'ASSIGN',
  'DRIVER_RESPONSE',
  'INSTITUTION_COMMENT',
] as const;
export type ComplaintEventType = (typeof COMPLAINT_EVENT_TYPES)[number];
export const COMPLAINT_EVENT_LABELS: Record<ComplaintEventType, string> = {
  CREATED: 'Şikayet oluşturuldu',
  STATUS: 'Durum değişti',
  REPLY: 'Yolcuya yanıt',
  NOTE: 'İç not',
  ASSIGN: 'Atama',
  DRIVER_RESPONSE: 'Şoför açıklaması',
  INSTITUTION_COMMENT: 'Kurum yorumu',
};
export const PASSENGER_COMPLAINT_EVENTS: ComplaintEventType[] = ['CREATED', 'STATUS', 'REPLY', 'DRIVER_RESPONSE'];
export const INSTITUTION_COMPLAINT_EVENTS: ComplaintEventType[] = [
  ...PASSENGER_COMPLAINT_EVENTS,
  'INSTITUTION_COMMENT',
];
/** "İlk yanıt" sayılan olaylar: yolcunun gördüğü firma işlemi (iç not ve atama sayılmaz). */
export const RESPONSE_COMPLAINT_EVENTS: ComplaintEventType[] = ['STATUS', 'REPLY'];

// ───────── SLA ─────────

/** Şikayet hizmet süreleri (saat). Güvenlik şikayetinde ilk yanıt 1 saat. */
export const COMPLAINT_SLA = { firstResponseH: 4, safetyFirstResponseH: 1, resolveH: 48 } as const;

export type ComplaintSlaState = 'ok' | 'warn' | 'breach' | 'met' | 'missed';

export interface ComplaintSla {
  state: ComplaintSlaState;
  /** açıkken hangi süre işliyor: ilk yanıt ya da çözüm; kapalıysa `done` */
  kind: 'response' | 'resolve' | 'done';
  /** saat: açıkken açılıştan bu yana, kapalıysa kapanış süresi */
  ageH: number;
  dueAt: string | null;
  label: string;
}

/** "45 dk", "3 sa", "2 gün 4 sa" */
export function hoursLabel(h: number): string {
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} dk`;
  if (h < 24) return `${Math.round(h)} sa`;
  const d = Math.floor(h / 24);
  const rest = Math.round(h - d * 24);
  return rest ? `${d} gün ${rest} sa` : `${d} gün`;
}

const ms = (d: string | Date) => (typeof d === 'string' ? Date.parse(d) : d.getTime());

/**
 * SLA durumu: yanıtsız şikayette ilk yanıt süresi (yarısı geçince uyarı), yanıtlanmışta çözüm süresi (%75'te uyarı).
 * Kapalı şikayette çözüm süresine uyuldu mu.
 */
export function complaintSla(
  c: {
    status: ComplaintStatus;
    category: ComplaintCategory;
    createdAt: string | Date;
    firstResponseAt: string | Date | null;
    resolvedAt: string | Date | null;
  },
  now = Date.now(),
): ComplaintSla {
  const created = ms(c.createdAt);
  const H = 3_600_000;
  if (CLOSED_COMPLAINT_STATUSES.includes(c.status)) {
    const closed = c.resolvedAt ? ms(c.resolvedAt) : now;
    const ageH = Math.max(0, (closed - created) / H);
    const met = ageH <= COMPLAINT_SLA.resolveH;
    return {
      state: met ? 'met' : 'missed',
      kind: 'done',
      ageH,
      dueAt: null,
      label: `${hoursLabel(ageH)} içinde kapandı`,
    };
  }
  const ageH = Math.max(0, (now - created) / H);
  if (!c.firstResponseAt) {
    const limit = SAFETY_COMPLAINT_CATEGORIES.includes(c.category)
      ? COMPLAINT_SLA.safetyFirstResponseH
      : COMPLAINT_SLA.firstResponseH;
    const state = ageH >= limit ? 'breach' : ageH >= limit / 2 ? 'warn' : 'ok';
    return {
      state,
      kind: 'response',
      ageH,
      dueAt: new Date(created + limit * H).toISOString(),
      label:
        state === 'breach' ? 'İlk yanıt gecikti' : state === 'warn' ? 'İlk yanıt süresi doluyor' : 'Yanıt bekliyor',
    };
  }
  const limit = COMPLAINT_SLA.resolveH;
  const state = ageH >= limit ? 'breach' : ageH >= limit * 0.75 ? 'warn' : 'ok';
  return {
    state,
    kind: 'resolve',
    ageH,
    dueAt: new Date(created + limit * H).toISOString(),
    label: state === 'breach' ? 'Çözüm süresi aşıldı' : state === 'warn' ? 'Çözüm süresi doluyor' : 'Süre içinde',
  };
}

// ───────── C49 / B17 şikayet listesi ─────────

export const COMPLAINT_PRIORITIES = ['safety', 'sla'] as const;
export type ComplaintPriority = (typeof COMPLAINT_PRIORITIES)[number];
export const COMPLAINT_PRIORITY_LABELS: Record<ComplaintPriority, string> = {
  safety: 'Güvenlik',
  sla: 'SLA riski',
};

export const ComplaintBoardQuery = z.object({
  status: z.enum(COMPLAINT_STATUSES).optional(),
  category: z.enum(COMPLAINT_CATEGORIES).optional(),
  institutionId: Id.optional(),
  routeId: Id.optional(),
  driverId: Id.optional(),
  priority: z.enum(COMPLAINT_PRIORITIES).optional(),
  q: z.string().trim().max(80).optional(),
  /** kapanan (çözüldü/reddedildi) şikayetler son kaç gün */
  closedDays: z.coerce.number().int().min(1).max(365).default(30),
});
export type ComplaintBoardQuery = z.infer<typeof ComplaintBoardQuery>;

export interface QualityOption {
  id: string;
  label: string;
}

export interface ComplaintRow {
  id: string;
  number: string;
  category: ComplaintCategory;
  status: ComplaintStatus;
  /** yolcu metninin ilk satırı (kısaltılmış) */
  excerpt: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  firstResponseAt: string | null;
  institution: { id: string; name: string };
  route: { id: string; code: string; name: string } | null;
  driver: { id: string; name: string } | null;
  plate: string | null;
  tripId: string | null;
  tripDate: string | null;
  /** firma ekibinden sorumlu kişi; kurum görünümünde gizli */
  assignee: { id: string; name: string } | null;
  safety: boolean;
  sla: ComplaintSla;
  institutionComments: number;
}

export interface ComplaintBoard {
  items: ComplaintRow[];
  /** süzgeçlerden sonra sütun sayıları */
  counts: Record<ComplaintStatus, number>;
  summary: { open: number; safety: number; slaBreach: number; slaWarn: number; unassigned: number };
  facets: { institutions: QualityOption[]; routes: QualityOption[]; drivers: QualityOption[] };
  closedDays: number;
  /** liste üst sınıra takıldı (en yeni kayıtlar gösterilir) */
  truncated: boolean;
  viewer: 'COMPANY' | 'INSTITUTION';
}

// ───────── C50 şikayet detayı ─────────

export interface ComplaintEventItem {
  id: string;
  type: ComplaintEventType;
  body: string | null;
  at: string;
  /** yazan: firma görünümünde ekip üyesi adı; kurum görünümünde firma adı; yolcu için "Yolcu"/"Veli" */
  by: string | null;
  byRole: 'PASSENGER' | 'COMPANY' | 'INSTITUTION' | 'SYSTEM';
  /** yolcu bu olayı görmez */
  hiddenFromPassenger: boolean;
  /** kurum bu olayı görmez (iç not, atama) */
  internal: boolean;
}

export interface ComplaintDetail extends ComplaintRow {
  text: string;
  photos: string[];
  /** kişisel veri en aza indirilmiş başvuran bilgisi (telefon, adres yok) */
  reporter: {
    label: string;
    relation: 'PASSENGER' | 'GUARDIAN';
    passengerKind: 'EMPLOYEE' | 'STUDENT' | 'GENERAL' | null;
    group: string | null;
  };
  trip: {
    id: string;
    date: string;
    direction: Direction;
    routeCode: string;
    routeName: string;
    plate: string;
    driver: string | null;
    plannedStartAt: string;
    delayMin: number;
    status: TripStatus;
  } | null;
  /** şikayet anında otomatik eklenen yolculuk verisi */
  snapshot: {
    date: string | null;
    route: string | null;
    plate: string | null;
    driver: string | null;
    delayMin: number | null;
    myStopPlanned: string | null;
    myStopArrived: string | null;
  } | null;
  driverResponse: string | null;
  resolution: string | null;
  events: ComplaintEventItem[];
  allowed: ComplaintStatus[];
  /** atanabilir ekip üyeleri (yalnız firma görünümünde) */
  assignees: { id: string; name: string; role: string }[];
  viewer: 'COMPANY' | 'INSTITUTION';
  company: string;
}

// ───────── C50 girdiler ─────────

const Text = (min: number, max: number, msg: string) => z.string().trim().min(min, msg).max(max);

export const ComplaintStatusInput = z
  .object({
    status: z.enum(COMPLAINT_STATUSES),
    /** yolcu da görür; reddetme ve yeniden açmada zorunlu */
    reason: z.string().trim().max(500).optional(),
    /** çözüm metni: yolcuya bildirilir ve e-postayla gönderilir */
    resolution: z.string().trim().max(1000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'RESOLVED' && (v.resolution ?? '').length < 10)
      ctx.addIssue({
        code: 'custom',
        path: ['resolution'],
        message: 'Çözüm için yolcuya gidecek kısa bir açıklama yazın (en az 10 karakter).',
      });
    if (v.status === 'REJECTED' && (v.reason ?? '').length < 5)
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'Reddetme gerekçesini yazın; yolcu bu gerekçeyi görür.',
      });
  });
export type ComplaintStatusInput = z.infer<typeof ComplaintStatusInput>;

export const ComplaintMessageInput = z.object({
  body: Text(2, 1000, 'Mesaj boş olamaz.'),
});
export type ComplaintMessageInput = z.infer<typeof ComplaintMessageInput>;

export const ComplaintAssignInput = z.object({ assigneeUserId: Id.nullable() });
export type ComplaintAssignInput = z.infer<typeof ComplaintAssignInput>;

// ───────── C51 / B18 puanlar ─────────

export const QUALITY_PERIODS = [7, 30, 90] as const;
const Days = z.coerce
  .number()
  .int()
  .refine((d) => (QUALITY_PERIODS as readonly number[]).includes(d), 'Dönem 7, 30 veya 90 gün olmalı')
  .default(30);

export const RatingQuery = z.object({
  days: Days,
  stars: z.coerce.number().int().min(1).max(5).optional(),
  tag: z.enum(RATING_TAGS).optional(),
  routeId: Id.optional(),
  driverId: Id.optional(),
  institutionId: Id.optional(),
  /** yalnız yorumlu puanlar */
  comments: z.enum(['all', 'with']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type RatingQuery = z.infer<typeof RatingQuery>;

export const RatingExportQuery = RatingQuery.omit({ page: true, pageSize: true }).extend({
  format: z.enum(['csv', 'pdf']).default('csv'),
});

/** Puan satırı: yolcu kimliği yok (anonim). Tarih = puanlanan yolculuğun günü. */
export interface RatingRow {
  id: string;
  stars: number;
  tags: RatingTag[];
  comment: string | null;
  tripId: string;
  tripDate: string;
  direction: Direction | null;
  route: { id: string; code: string; name: string } | null;
  driver: { id: string; name: string } | null;
  plate: string | null;
  institution: string;
  createdAt: string;
}

export interface RatingGroup {
  id: string;
  label: string;
  sub: string | null;
  count: number;
  average: number;
  /** 1–2 yıldız */
  low: number;
  complaints: number;
}

export interface RatingSummary {
  count: number;
  average: number | null;
  prevCount: number;
  prevAverage: number | null;
  withComment: number;
  /** 5 → 1 */
  distribution: { stars: number; count: number }[];
  tags: { tag: RatingTag; praised: number; criticized: number }[];
  byDriver: RatingGroup[];
  byRoute: RatingGroup[];
  /** 7/30 gün: günlük, 90 gün: haftalık */
  trend: { date: string; label: string; count: number; average: number | null }[];
}

export interface RatingPage extends Paged<RatingRow> {
  days: number;
  from: string;
  to: string;
  summary: RatingSummary;
  facets: { institutions: QualityOption[]; routes: QualityOption[]; drivers: QualityOption[] };
}

// ───────── C52 kalite paneli ─────────

export const QualityOverviewQuery = z.object({ days: Days });

export interface QualityOverview {
  days: number;
  from: string;
  to: string;
  kpis: {
    openComplaints: number;
    openSafety: number;
    unassigned: number;
    complaints: number;
    prevComplaints: number;
    avgFirstResponseH: number | null;
    prevAvgFirstResponseH: number | null;
    avgResolutionH: number | null;
    prevAvgResolutionH: number | null;
    /** dönemde kapanan şikayetlerin SLA'ya uyma oranı */
    slaMetPct: number | null;
    slaBreaches: number;
    avgRating: number | null;
    prevAvgRating: number | null;
    ratingCount: number;
  };
  byCategory: { category: ComplaintCategory; count: number; open: number }[];
  trend: { date: string; label: string; complaints: number; ratings: number; avgRating: number | null }[];
  worstRoutes: RatingGroup[];
  worstDrivers: RatingGroup[];
  breaches: ComplaintRow[];
}
